const DISBURSEMENT_GRAPH_KEYS = new Set([
  "disbursement",
  "disbWithAdvance",
  "accumulatedDisbursement",
  "advancementTo",
  "advancementFrom",
]);

export const AMPLI_DISBURSEMENT_SOURCE = "ampli_disbursement";

const isAmpliSource = (source) => source === AMPLI_DISBURSEMENT_SOURCE;

// No modo Ampli o desembolso do Sienge e todo o vocabulário de antecipação saem
// do gráfico: a curva é outra, e misturar as duas somaria planejamentos distintos.
const AMPLI_HIDDEN_GRAPH_KEYS = new Set([
  ...DISBURSEMENT_GRAPH_KEYS,
  "takenTo",
  "takenFrom",
]);
const AMPLI_GRAPH_KEYS = ["physicals", "ampliDisbursement"];
const AMPLI_COLUMN_KEYS = ["physical", "ampliDisbursement"];

// Séries que saíram do dashboard. Revisão de 2026-10-04: o "Desembolso projetado"
// (pago + a pagar + restante) disputava o olhar com o Desembolso (Ampli), que é a curva
// que vale. Revisão de 2026-10-08: o "Desembolso Acumulado" saiu do Fluxo de Caixa.
// Filtradas por último nos gráficos, nas colunas e na exportação, então uma configuração
// já salva com elas também deixa de mostrá-las.
const RETIRED_SERIES_KEYS = new Set(["cashProjection", "accumulatedDisbursement"]);
export const dropRetiredSeries = (keys = []) =>
  (Array.isArray(keys) ? keys : []).filter((key) => !RETIRED_SERIES_KEYS.has(key));
const dropRetired = dropRetiredSeries;

export const AMPLI_SERIES_LABELS = Object.freeze({
  ampliDisbursement: "Previsão de desembolso",
  cashProjection: "Desembolso projetado",
});
export const AMPLI_CASH_PROJECTION_DESCRIPTION = "Saídas da obra: pago + a pagar + previsto restante ainda não comprometido. Não inclui recebíveis nem representa saldo de caixa.";

// As duas séries que a curva Ampli acrescenta ao gráfico e à exportação. Fica aqui, ao
// lado dos rótulos, para o gráfico não redeclarar a mesma lista.
export const AMPLI_CURVE_KEYS = Object.freeze(["ampliDisbursement", "cashProjection"]);

// Estados de `ampliCurveState` em que as duas séries podem aparecer: `stale` ainda mostra
// a curva anterior, enquanto `inconsistent`/`unavailable` não têm curva nenhuma para
// desenhar — e uma série zerada afirmaria "Desembolso Ampli: R$ 0,00".
export const AMPLI_VISIBLE_CURVE_STATES = Object.freeze(["ready", "stale"]);

const unique = (values) => [...new Set(values)];

const selectedSource = (policy) =>
  policy?.effectiveSource ?? policy?.requestedSource ?? null;

// Previsto, medido e físico-financeiro são a MESMA série (`resolvedSchedule` em
// `getDetail.js`) sob três rótulos: um previsto físico-financeiro aplicado (Prevision)
// ou lido do ERP. Quando a obra não tem nenhum, `physicalFinancial.available` (que o
// BE já decide da série completa, antes de qualquer recorte por período) é `false`, e
// nenhuma das chaves abaixo deve aparecer — em vez de série/coluna zerada
// (serve-works-without-optional-sources, D4/D5).
export const isPhysicalFinancialAvailable = (policy) => (
  policy?.physicalFinancial?.available === true
);
const PHYSICAL_SCHEDULE_GRAPH_KEYS = new Set(["physicals"]);
const PHYSICAL_SCHEDULE_COLUMN_KEYS = new Set(["physical", "predicted", "baseline", "measured"]);
const dropAbsentSchedule = (keys, policy, scheduleKeys) => (
  isPhysicalFinancialAvailable(policy) ? keys : keys.filter((key) => !scheduleKeys.has(key))
);

/**
 * Apply the organization's planning policy to one configured chart.
 *
 * `ensurePlanningSeries` decides whether the chosen planning curve may be added when the
 * user's configuration omits it. Only the projection chart wants that; the advancements
 * and balance charts must stay subtractive, otherwise the policy injects a cash-flow
 * curve into a chart that is not about cash-flow planning at all.
 */
export const filterCashFlowGraphSeries = (
  configured = [],
  policy,
  { ensurePlanningSeries = true } = {},
) => {
  const values = Array.isArray(configured) ? configured : [];
  const source = selectedSource(policy);
  const ensure = (keys) => (ensurePlanningSeries ? keys : []);
  const result = (() => {
    if (source === "physical_financial") {
      return unique([
        ...ensure(["physicals"]),
        ...values.filter(
          (key) => !DISBURSEMENT_GRAPH_KEYS.has(key) && key !== "physicals",
        ),
      ]);
    }
    if (isAmpliSource(source)) {
      return unique([
        ...ensure(AMPLI_GRAPH_KEYS),
        ...values.filter(
          (key) => !AMPLI_HIDDEN_GRAPH_KEYS.has(key) && !AMPLI_GRAPH_KEYS.includes(key),
        ),
      ]);
    }
    if (source === "disbursement") {
      return unique([
        ...ensure(["disbursement"]),
        ...values.filter((key) => key !== "physicals" && key !== "disbursement"),
      ]);
    }
    // Nothing competes in the combined view, so no series is removed: the disbursement
    // chart keeps its accumulated curve and the advancements chart keeps the series it is
    // built from. Only the ordering is fixed so the two planning curves come first.
    if (source === "combined") {
      return unique([...ensure(["physicals", "disbursement"]), ...values]);
    }
    return [...values];
  })();
  // Applied last, regardless of branch or `ensure()`: an obra with no previsto/medido/
  // físico-financeiro at all must never show "physicals", even when the chosen policy
  // would otherwise inject it (serve-works-without-optional-sources, D4/D5).
  return dropRetired(
    dropAbsentSchedule(result, policy, PHYSICAL_SCHEDULE_GRAPH_KEYS),
  );
};

export const filterCashFlowColumns = (configured = [], policy) => {
  const values = Array.isArray(configured) ? configured : [];
  const source = selectedSource(policy);
  const result = (() => {
    if (isAmpliSource(source)) {
      return unique([
        ...AMPLI_COLUMN_KEYS,
        ...values.filter((key) => !AMPLI_COLUMN_KEYS.includes(key) && key !== "disbursement"),
      ]);
    }
    if (source === "physical_financial") {
      return unique([
        "physical",
        ...values.filter(
          (key) => key !== "physical" && key !== "disbursement" && !AMPLI_CURVE_KEYS.includes(key),
        ),
      ]);
    }
    if (source === "disbursement") {
      return unique([
        "disbursement",
        ...values.filter(
          (key) => key !== "physical" && key !== "disbursement" && !AMPLI_CURVE_KEYS.includes(key),
        ),
      ]);
    }
    if (source === "combined") {
      return unique([
        "physical",
        "disbursement",
        ...values.filter(
          (key) => key !== "physical" && key !== "disbursement" && !AMPLI_CURVE_KEYS.includes(key),
        ),
      ]);
    }
    // Sem política escolhida (`selection_required`) as duas colunas também não podem
    // aparecer: elas só existem quando a organização de fato optou pela curva Ampli.
    return values.filter((key) => !AMPLI_CURVE_KEYS.includes(key));
  })();
  // Applied last: `physical`/`predicted`/`baseline`/`measured` are the same absent
  // series under four column names, and none of the branches above gate them on
  // whether the obra actually has one (serve-works-without-optional-sources, D4/D5).
  return dropRetired(
    dropAbsentSchedule(result, policy, PHYSICAL_SCHEDULE_COLUMN_KEYS),
  );
};

export const filterCashFlowExport = (
  { fields = [], resources = [] } = {},
  policy,
  { physicalsSource, physicalsLabel = null } = {},
) => {
  const source = selectedSource(policy);
  const filteredFields = (Array.isArray(fields) ? fields : [])
    .filter((field) => !RETIRED_SERIES_KEYS.has(field.key))
    // Obra sem físico-financeiro: a coluna sai da planilha em vez de sair zerada, como no
    // gráfico e na tabela (financial-projection-integration, revisão de 2026-10-13).
    .filter((field) => isPhysicalFinancialAvailable(policy) || !PHYSICAL_SCHEDULE_GRAPH_KEYS.has(field.key))
    .filter((field) => {
      if (isAmpliSource(source)) {
        return !AMPLI_HIDDEN_GRAPH_KEYS.has(field.key);
      }
      if (source === "physical_financial") {
        return !DISBURSEMENT_GRAPH_KEYS.has(field.key);
      }
      if (source === "disbursement") return field.key !== "physicals";
      return true;
    })
    .map((field) => {
      if (isAmpliSource(source) && AMPLI_SERIES_LABELS[field.key]) {
        return { ...field, label: AMPLI_SERIES_LABELS[field.key] };
      }
      if (isAmpliSource(source) && field.key === "physicals") {
        return { ...field, label: physicalFinancialLabel(policy, physicalsSource, physicalsLabel) };
      }
      if (["physical_financial", "combined"].includes(source) && field.key === "physicals") {
        return {
          ...field,
          label: source === "combined" || resolvePhysicalsSource(policy, physicalsSource)
            ? physicalFinancialLabel(policy, physicalsSource, physicalsLabel)
            : "Previsão de caixa (Previsto)",
        };
      }
      if (["disbursement", "combined"].includes(source) && field.key === "disbursement") {
        return {
          ...field,
          label:
            policy?.state === "needs_adjustment"
              ? "Desembolso (em ajuste)"
              : "Previsão de caixa (Desembolso)",
        };
      }
      return { ...field };
    });
  const keys = new Set(filteredFields.map((field) => field.key));
  const filteredResources = (Array.isArray(resources) ? resources : []).map(
    (row) =>
      Object.fromEntries(Object.entries(row).filter(([key]) => keys.has(key))),
  );
  return { fields: filteredFields, resources: filteredResources };
};

export const isCashFlowRawExportVisible = (exportType, policy) => {
  const source = selectedSource(policy);
  if (isAmpliSource(source)) {
    return !["disbursements", "advancements"].includes(exportType);
  }
  if (exportType === "ampli_disbursements") return false;
  if (source === "physical_financial") {
    return !["disbursements", "advancements"].includes(exportType);
  }
  if (source === "disbursement") return exportType !== "physical_financials";
  if (source === "combined") return true;
  return true;
};

/**
 * Whether the disbursement journey is read-only under the organization's policy.
 *
 * Only the physical-financial view retires the disbursement schedule; the combined view
 * keeps advancements editable, because its whole point is that both curves stay live.
 * Read `requestedSource`, not `effectiveSource`: the policy is what the organization
 * decided, while the effective source also reflects how complete the coverage happens
 * to be right now.
 */
export const isDisbursementPlanningReadOnly = (policy) => (
  policy?.requestedSource === "physical_financial"
);

/**
 * Whether the organization's policy is set to the Ampli disbursement curve.
 *
 * Reads `requestedSource` — the organization's policy choice — not `effectiveSource`
 * (via `selectedSource()`, used by the series/column/export filters above): the policy
 * is what was decided, regardless of whether the BE could actually serve that curve
 * right now (e.g. it may still report `effectiveSource: null` while coverage catches up).
 */
export const isAmpliDisbursementPolicy = (policy) => (
  isAmpliSource(policy?.requestedSource)
);

export const shouldShowDisbursementCoverage = (policy) => {
  if (isAmpliDisbursementPolicy(policy)) return false;
  if (policy?.availableSources?.disbursement !== true) return false;
  return policy.requestedSource !== "physical_financial";
};

export const cashFlowPlanningSourceLabel = (source) =>
  source === "disbursement"
    ? "Cronograma de desembolso"
    : source === "physical_financial"
    ? "Previsto"
    : source === "combined"
    ? "Visão completa"
    : isAmpliSource(source)
    ? "Previsão de desembolso"
    : "Fonte ainda não escolhida";

// Fonte do físico-financeiro da obra inteira (`physicals`): o previsto aplicado do
// Prevision ou o físico-financeiro do ERP (Sienge ou MEGA). O `/detail` publica
// `physicalsSource: "prevision" | "erp" | null` ao lado de `physicals`; um payload sem o
// campo (cache antigo) ainda diz a fonte em `cashFlowPlanning.physicalFinancial.source`.
export const resolvePhysicalsSource = (policy, physicalsSource) => {
  if (["prevision", "erp", "mega"].includes(physicalsSource)) return physicalsSource;
  const source = String(policy?.physicalFinancial?.source ?? "").toLowerCase();
  if (!source || source === "none") return null;
  return source === "prevision" ? "prevision" : "erp";
};

const PHYSICALS_SOURCE_LABELS = Object.freeze({
  prevision: "Previsto (Prevision)",
  erp: "Previsto (ERP)",
  mega: "Previsto (MEGA)",
});

// Revisão de 2026-10-13 (D9): o mesmo rótulo no gráfico, na tabela, no acordeão e na
// exportação, no lugar do "Previsto (Sienge)" e do "Físico Financeiro" que conviviam.
// `physicalsLabel` (o nome pronto do `/detail`, que conhece o ERP: "Previsto (Prevision)" na
// obra MEGA) vence; sem ele, o nome sai da fonte.
export const physicalFinancialLabel = (policy, physicalsSource, physicalsLabel = null) => (
  (typeof physicalsLabel === "string" && physicalsLabel !== "" ? physicalsLabel : null)
    ?? PHYSICALS_SOURCE_LABELS[resolvePhysicalsSource(policy, physicalsSource)]
    ?? "Previsto"
);

// Atalho para quem tem o projeto do `/detail` inteiro em mãos.
export const projectPhysicalsLabel = (project) => (
  physicalFinancialLabel(project?.cashFlowPlanning, project?.physicalsSource, project?.physicalsLabel)
);

/**
 * Rótulo da coluna/linha `physical` da tabela e do acordeão. Sob a política Ampli (e nas
 * duas políticas de físico-financeiro) a coluna é o físico-financeiro da obra, com a
 * fonte; nas demais segue o "Físico" de sempre.
 */
export const physicalColumnLabel = (project) => {
  const policy = project?.cashFlowPlanning;
  const source = selectedSource(policy);
  return isAmpliSource(source) || ["physical_financial", "combined"].includes(source)
    ? projectPhysicalsLabel(project)
    : "Físico";
};

/**
 * Por que a série do físico-financeiro não está no gráfico — para o ícone de informação.
 * `null` quando a obra tem físico-financeiro (a série aparece, mesmo com a curva de
 * desembolso indisponível) ou quando a política não é a Ampli.
 */
export const physicalFinancialAbsenceNote = (project) => {
  const policy = project?.cashFlowPlanning;
  if (!isAmpliDisbursementPolicy(policy)) return null;
  if (isPhysicalFinancialAvailable(policy)) return null;
  if (String(project?.erp ?? "").toUpperCase() === "MEGA") {
    return "A obra não tem previsto (nem do Prevision nem o mensal do MEGA), por isso a série não aparece no gráfico.";
  }
  return "A obra não tem Previsto (Prevision ou ERP), por isso a série não aparece no gráfico.";
};

/**
 * Whether the Ampli coverage ruler should show, in place of the Sienge one.
 *
 * Same `requestedSource` reasoning as `isAmpliDisbursementPolicy` above: this keys off
 * the organization's policy choice, not `selectedSource()` (`effectiveSource ?? requestedSource`),
 * which is what the series/column/export filters use to reflect what the BE can serve today.
 */
export const shouldShowAmpliCoverage = (policy) => isAmpliDisbursementPolicy(policy);

const AMPLI_STATE_LABELS = Object.freeze({
  ready: "Curva atualizada",
  stale: "Curva desatualizada",
  inconsistent: "Curva inconsistente",
  unavailable: "Curva indisponível",
});

const AMPLI_REASON_LABELS = Object.freeze({
  negative_planned: "O previsto tem mês negativo; corrija o cronograma antes de gerar a curva.",
  leaf_not_service: "Há previsto fora do nível de serviço; a EAP precisa ser revista.",
  invariant_root_total: "A soma da curva não bate com o previsto da obra.",
  invariant_leaf_total: "A soma da curva não bate com o previsto de um serviço.",
  invariant_negative: "A geração produziu valor negativo.",
  invariant_cash_total: "O desembolso projetado não fecha com pago, a pagar e restante.",
  // Revisão de 2026-10-13 (D2): sem escolha gravada, a origem é a primeira fonte utilizável
  // da cadeia Prevision → físico-financeiro do ERP → orçamento.
  no_canonical_planned: "A obra não tem fonte para a curva: nem Prevision aplicado, nem Previsto do ERP, nem orçamento.",
  composition_degraded: "A composição de origem está degradada; a curva não pôde ser gerada.",
  composition_missing: "A obra não tem a composição do orçamento carregada.",
  // erp-baseline-sources (D7/D8/D11): a origem do desembolso é escolhida POR OBRA e nunca
  // troca sozinha. Quando a origem em uso não pode ser usada, a curva anterior segue no ar e
  // o motivo vem aqui — a tela oferece a troca de origem ao lado.
  sienge_disbursement_missing: "A obra não tem desembolso do ERP carregado.",
  sienge_disbursement_coverage_inconsistent: "A cobertura do desembolso do ERP está inconsistente.",
  sienge_disbursement_coverage_gap: "A cobertura do desembolso do ERP está incompleta.",
  sienge_disbursement_coverage_price_informed: "Parte do desembolso do ERP tem preço informado e não está classificada.",
  sienge_disbursement_coverage_remaining: "Parte do desembolso do ERP ainda está sem classificar (restante).",
  sienge_disbursement_coverage_unclassified: "Parte do desembolso do ERP está sem classificação.",
  erp_without_disbursement: "O ERP desta obra não fornece desembolso.",
  // Motivos das fontes descartadas da cadeia (`origin.discarded[].reason`).
  prevision_not_enabled: "A obra não usa o Prevision.",
  prevision_not_applied: "A obra não tem um cronograma do Prevision aplicado.",
  prevision_without_service_curves: "O cronograma aplicado do Prevision não tem curva no nível de serviço.",
  physical_financial_missing: "A obra não tem Previsto do ERP carregado.",
  physical_financial_without_services: "A última medição do Previsto do ERP não tem serviços.",
  mega_predicted_missing: "O MEGA ainda não publica o Previsto desta obra.",
  planned_series_missing: "A obra não tem Previsto mensal carregado.",
  budget_missing: "A obra não tem orçamento carregado.",
  calculation_snapshot_unavailable:
    "A última carga de dados da obra está inconsistente; a curva anterior continua valendo até a próxima carga.",
  budget_without_distribution: "Defina a distribuição no tempo por nível da EAP para gerar a curva a partir do orçamento.",
});

export const ampliCurveStateLabel = (status) => (
  AMPLI_STATE_LABELS[status] ?? AMPLI_STATE_LABELS.unavailable
);

export const ampliInconsistentReasonLabel = (reason) => (
  AMPLI_REASON_LABELS[reason] ?? "A última geração falhou e a curva anterior segue no ar."
);

const ORIGIN_KIND_LABELS = Object.freeze({
  prevision: "Prevision",
  physical_financial: "Previsto do ERP",
  erp_disbursement: "Desembolso do ERP",
  budget: "Orçamento",
});

/**
 * As fontes da cadeia padrão que não foram usadas e o porquê, em PT-BR
 * (`ampliDisbursement.origin.discarded`, contrato C1). Motivo desconhecido vira texto
 * genérico em vez de sumir: a lista explica "por que não o físico-financeiro".
 */
export const discardedOriginLabels = (origin) => (
  (Array.isArray(origin?.discarded) ? origin.discarded : [])
    .filter((item) => item?.kind)
    .map(({ kind, reason }) => ({
      kind,
      reason: reason ?? null,
      label: `${ORIGIN_KIND_LABELS[kind] ?? kind}: ${
        AMPLI_REASON_LABELS[reason] ?? "fonte indisponível nesta obra."
      }`,
    }))
);

/**
 * Estado da curva Ampli para o cabeçalho do módulo e para o card de cobertura.
 * `getDetail` publica `ampliDisbursement: { state, coverage, generatedAt, reason }`;
 * quando ele não vier (payload antigo em cache), a política ainda diz o suficiente.
 *
 * O fallback lê `policy.requestedSource`/`policy.state` — a escolha de política do
 * organizador — e não `selectedSource()` (`effectiveSource ?? requestedSource`), que é o
 * que os filtros de série/coluna/exportação usam para refletir o que o BE consegue
 * servir agora; aqui queremos saber qual curva a organização escolheu, mesmo quando o
 * bloco dedicado do `getDetail` ainda não confirma que ela está pronta.
 */
/**
 * Whether the two Ampli curve columns/rows/series may show a real value for this
 * project right now — `ready`/`stale` still have a curve to display, while
 * `inconsistent`/`unavailable` have none, so callers must pass `null` instead of a
 * zeroed value. Single source of truth for the predicate that TableItem,
 * AccordionItem and FinancialBarChart each used to retype.
 */
export const isAmpliCurveVisible = (project) => (
  AMPLI_VISIBLE_CURVE_STATES.includes(ampliCurveState(project).status)
);

export const ampliCurveState = (project) => {
  const block = project?.ampliDisbursement ?? null;
  const policy = project?.cashFlowPlanning ?? null;
  const generatedAt = block?.generatedAt ?? null;
  // O BE só publica `degraded_source` (composição de origem recusada, ver
  // readAmpliCurve.js) quando AINDA está servindo uma curva anterior — sem curva
  // nenhuma pra servir, ele responde `unavailable` diretamente. Por isso o estado
  // vira "stale" (curva antiga no ar) e não "unavailable": os 4 estados existentes
  // acima seguem mapeados 1:1, sem mudança, isto é aditivo.
  if (block?.state === "degraded_source") {
    return { status: "stale", reason: block.reason ?? null, generatedAt };
  }
  if (block?.state && ["ready", "stale", "inconsistent", "unavailable"].includes(block.state)) {
    return { status: block.state, reason: block.reason ?? null, generatedAt };
  }
  if (!isAmpliDisbursementPolicy(policy)) {
    return { status: "unavailable", reason: null, generatedAt };
  }
  if (policy.state === "inconsistent") {
    return { status: "inconsistent", reason: policy.reason ?? null, generatedAt };
  }
  if (policy.state === "ready") {
    return {
      status: policy.condition === "stale" ? "stale" : "ready",
      reason: null,
      generatedAt,
    };
  }
  return { status: "unavailable", reason: policy.reason ?? null, generatedAt };
};
